import { Container, Dimmer, Header, Icon, Loader, Segment } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { LoadingContext, ModeReglementContext } from '@/contexts';
import { useEncaissement } from '@/hooks';
import { getFacture } from '@/routes';
import type { FactureType } from '@/types';
import { getInfosEncaissementDirect, marquerEncaissementTermine } from '@/utils';
import { ModalEncaissement } from '../Encaissement/ModalEncaissement';
import ModalEncaisseDepassee from '../Encaissement/ModalEncaissement/ModalEncaisseDepassee';

const EncaissementDirect = () => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [facture, setFacture] = React.useState<FactureType>(null);
  const [encaissementTermine, setEncaissementTermine] = React.useState<boolean>(false);
  const [accessForbidden, setAccessForbidden] = React.useState<boolean>(false);

  /** Objet des informations si on est en embedded (encaissement direct) */
  const {
    modesReglementsLoadings: { regieModesReglement: regieMRLoading },
  } = React.useContext(LoadingContext);
  const { regieModesReglement, getRegieModesReglement } = React.useContext(ModeReglementContext);

  const { saveEncaissement, encaisseDepassee, resetEncaisseDepassee } = useEncaissement();

  const loadFacture = React.useCallback(() => {
    const embeddedInfos = getInfosEncaissementDirect();

    if (embeddedInfos?.identifiantFacture) {
      // Si l'encaissement est déjà terminé, afficher le message de fin
      if (embeddedInfos.done === true) {
        setEncaissementTermine(true);
        return;
      }

      // Initialiser done à false
      marquerEncaissementTermine(false);

      setLoading(true);
      request
        .get(getFacture(embeddedInfos.identifiantFacture))
        .then((data: FactureType) => {
          setFacture(data);
        })
        .catch((error: any) => {
          if (error?.code === 403) {
            setAccessForbidden(true);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  const handleSave = React.useCallback(
    async (body: Record<string, unknown>, imprimerRecu?: boolean, envoyerMail?: boolean) => {
      const recu = await saveEncaissement(body, imprimerRecu, envoyerMail);

      if (recu) {
        // Mettre à jour done à true
        marquerEncaissementTermine(true);

        setEncaissementTermine(true);
      }
    },
    [saveEncaissement],
  );

  // Mémoisation du tableau de factures pour éviter les re-renders inutiles
  // qui causeraient le reset du loading dans ModalEncaissement
  const memoizedFactures = React.useMemo(() => (_.isNil(facture) ? [] : [facture]), [facture]);

  React.useEffect(() => {
    if (_.isNil(regieModesReglement) && !regieMRLoading) {
      getRegieModesReglement();
    }
  }, [getRegieModesReglement, regieMRLoading, regieModesReglement]);

  // On charge la facture qu'une fois qu'on a les modes de règlements de la régie
  React.useEffect(() => {
    if (!_.isNil(regieModesReglement) && _.isNil(facture)) {
      loadFacture();
    }
  }, [facture, loadFacture, regieModesReglement]);

  if (regieMRLoading) {
    return (
      <Dimmer active inverted>
        <Loader inverted>Chargement des modes de règlements de la régie...</Loader>
      </Dimmer>
    );
  }
  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader inverted>Chargement de la facture...</Loader>
      </Dimmer>
    );
  }

  if (accessForbidden) {
    return (
      <Container
        data-testid="access-forbidden-message"
        fluid
        style={{ paddingTop: 100, alignItems: 'center' }}
      >
        <Segment
          basic
          textAlign="center"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Header icon size="large" data-testid="prohibited-message">
            <Icon name="ban" size="massive" />
            Vous n&apos;êtes pas autorisé à accéder à cette page.
            <br />
            <br />
            <Header.Subheader>Veuillez contacter votre administrateur.</Header.Subheader>
          </Header>
        </Segment>
      </Container>
    );
  }

  if (encaissementTermine) {
    return (
      <Container
        data-testid="encaissement-termine-message"
        fluid
        style={{ paddingTop: 100, alignItems: 'center' }}
      >
        <Segment
          basic
          textAlign="center"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Header icon size="large" color="green">
            <Icon name="check circle" size="massive" color="green" />
            Encaissement terminé.
            <br />
            <br />
            <Header.Subheader>Vous pouvez fermer cet onglet.</Header.Subheader>
          </Header>
        </Segment>
      </Container>
    );
  }

  return (
    <>
      <ModalEncaissement factures={memoizedFactures} onSave={handleSave} looc />
      {encaisseDepassee && (
        <ModalEncaisseDepassee
          encaisseGlobaleDepassee={encaisseDepassee.encaisseGlobaleDepassee}
          encaisseNumeraireDepassee={encaisseDepassee.encaisseNumeraireDepassee}
          onClose={resetEncaisseDepassee}
        />
      )}
    </>
  );
};

export default EncaissementDirect;
